import { flow, observable } from "mobx";
import { awaitableWhen } from "../../utils/util";
import { BackendResponse } from "../BackendResponse";
import { NextApiParams, withNextApi } from "../nextApiHelpers";

export interface UpdateCandidateResponse extends BackendResponse {}

export interface UpdateCandidateParameters extends NextApiParams {
  fields: UpdateField[];
  /** Required by the backend when an email *or* phone field is being changed. */
  idpUserId?: string;
  /** Required by the backend when an email field is being changed (issued via
   * /auth/SendOtc). */
  oneTimeCode?: string;
  /** Required by the backend when the mobile-phone field is being changed
   * (issued via /auth/SendPhoneOtc). */
  phoneOneTimeCode?: string;
}

/** Backend message returned when a privileged change requires a one-time code.
 * Kept in sync with CvpUpdateCandidateController in webtempultra. */
export const MSG_ONE_TIME_CODE_REQUIRED = "OneTimeCode required";
export const MSG_PHONE_ONE_TIME_CODE_REQUIRED = "PhoneOneTimeCode required";

export type updateCandidate = (
  args: UpdateCandidateParameters
) => Promise<UpdateCandidateResponse>;

const _updateCandidate = withNextApi<
  UpdateCandidateResponse,
  UpdateCandidateParameters
>("/cvp/CvpUpdateCandidate", {
  f: async (args, f) => {
    let data = await f(args);

    if (data?.message) data.ok = false;
    if (data?.message)
      data.message = `Unable to update fields: ${data.message}`;

    return data;
  },
});

const queue = observable.array();

// wait until previous updateCandidate is done before starting a new one
export const updateCandidate: typeof _updateCandidate = flow(function* (args) {
  const f = () => _updateCandidate(args);
  queue.push(f);

  let result;
  try {
    yield awaitableWhen(() => queue[0] === f);

    result = yield f();
  } finally {
    queue.remove(f);
  }

  return result;
});

export interface UpdateField {
  fieldName: string;
  fieldValue: string | null;
}
